"use client";

import useScrollTo from "@/app/hooks/useScrollTo";
import AlgoliaCustomPagination from "@/app/vendor/[name]/[id]/algoliaCustomPagination";
import CustomConfigure from "@/components/customConfigure";
import ProductItem, { ProductHit } from "@/components/products-tile/product";
import { useAppDispatch } from "@/store/hooks";
import { getBestPrices } from "@/store/searchSlice";
import { DeviceType } from "@/types/search";
import { useWindowWidth } from "@react-hook/window-size";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHits, useInstantSearch } from "react-instantsearch";
import { NumericFormat } from "react-number-format";

import { useLocationDetails } from "@/app/hooks/useLocationDetails";
import style from "./vendorProductsList.module.scss";

export default function VendorProductsList({ vendorName }: VendorProductsListProps) {
  const { scrollToRef } = useScrollTo();
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const { hits, sendEvent } = useHits<ProductHit>();
  const {
    results: { nbHits, hitsPerPage, page: currentPage },
  } = useInstantSearch();
  const [deviceType, setDeviceType] = useState<DeviceType>("desktop");
  const windowWidth = useWindowWidth();

  const { zipCode } = useLocationDetails();

  useEffect(() => {
    let newDeviceType: DeviceType;
    if (windowWidth < 576) newDeviceType = "mobile";
    else if (windowWidth < 768) newDeviceType = "tablet";
    else newDeviceType = "desktop";
    if (newDeviceType != deviceType) setDeviceType(newDeviceType);
  }, [windowWidth, deviceType]);

  const onClickCallback = useCallback(() => {
    scrollToRef(ref);
  }, [scrollToRef]);

  useEffect(() => {
    if (hits.length > 0) {
      const mpIds = hits.map((hit): number => hit.mp_id);
      dispatch(getBestPrices({ mpIds: mpIds, postalCode: zipCode }));
    }
  }, [dispatch, hits, zipCode]);

  return (
    <div ref={ref} className={`${style.vendorProductList} ${hits.length === 0 ? "d-none" : ""}`}>
      <CustomConfigure
        filters={[
          {
            key: "vendor",
            value: vendorName,
          },
        ]}
        hitsPerPage={36}
        facets={[`category.lvl0:Dental Supplies`]}
      />
      <div className={`${style.header}`}>
        <div className={style.name}>{vendorName}</div>
        <div className={style.links}>
          <Link href={`/weekly-specials?facet.vendor=${vendorName}`} className="pe-2 pe-sm-0">
            {vendorName}&apos;s Weekly Specials
          </Link>
          <span className={`mx-1 d-none d-sm-inline-block`}>|</span>
          <Link href={`/search?facet.vendor=${vendorName}`}>Entire Product Offering</Link>
        </div>
      </div>
      <div className={style.totalResults}>
        <span className="d-none d-md-inline">
          <NumericFormat displayType="text" value={currentPage * hitsPerPage + 1} thousandSeparator={true} />
          {` - `}
          <NumericFormat
            displayType="text"
            value={Math.min((currentPage + 1) * hitsPerPage, nbHits)}
            thousandSeparator={true}
          />
          <span>{` of `}</span>
        </span>
        <span className="fw-bolder">
          <NumericFormat displayType="text" value={nbHits} thousandSeparator={true} />
        </span>
        <span>{` results`}</span>
      </div>
      <ul className={style.aisHitsList}>
        {hits.map((hit) => {
          return (
            <li key={hit.mp_id} className={style.aisHitsItem}>
              <ProductItem
                hit={hit}
                viewMode="grid"
                deviceType={deviceType}
                sendEvent={sendEvent}
                disableAddToCart
                hideBadges
                hideLastPurchased
                hidePrice
              />
            </li>
          );
        })}
      </ul>
      <div className={style.paginationContainer}>
        <AlgoliaCustomPagination onPageChange={onClickCallback} />
      </div>
    </div>
  );
}

export type VendorProductsListProps = {
  vendorName: string;
  // vendorId: string | number;
};
